import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PATH_DASHBOARD } from "../../routes/paths";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  categories: [],
  singleCategory: null,
  categoryPrinters: [],
};

export const createCategory = createAsyncThunk(
  "createCategory/category",
  async ({ data, enqueueSnackbar, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.post("categories", data);
      enqueueSnackbar("Category is added successfully");
      // Navigation happens in the form after the printer assignment is saved.
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchCategories = createAsyncThunk(
  "fetchCategories/category",
  async ({ enqueueSnackbar, limit = 1000, page = 0, status }, thunkApi) => {
    try {
      const response = await axiosInstance.get("categories", {
        params: {
          limit,
          page: page + 1,
          ...((status === true || status === false) && { active: status }),
        },
      });
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchSingleCategory = createAsyncThunk(
  "fetchSingleCategory/category",
  async ({ enqueueSnackbar, slug }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`categories/${slug}`);

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateCategory = createAsyncThunk(
  "updateCategory/category",
  async ({ slug, data, enqueueSnackbar, setOpenEdit }, thunkApi) => {
    try {
      const response = await axiosInstance.post(`categories/${slug}`, data);
      return {
        data: response.data.data,
        enqueueSnackbar,
        setOpenEdit,
        slug,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateCategoryPrinter = createAsyncThunk(
  "updateCategoryPrinter/category",
  async ({ slug, printer_id, enqueueSnackbar }, thunkApi) => {
    try {
      const response = await axiosInstance.put(
        `admin/categories/${slug}/printer`,
        { printer_id }
      );
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchCategoryPrinters = createAsyncThunk(
  "fetchCategoryPrinters/category",
  async ({ enqueueSnackbar }, thunkApi) => {
    try {
      const response = await axiosInstance.get("admin/settings/category-printers");
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "deleteCategory/category",
  async ({ id, enqueueSnackbar }, thunkApi) => {
    try {
      await axiosInstance.delete(`categories/${id}`);
      return { id, enqueueSnackbar };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState,
  extraReducers: (builder) => {
    // TODO: create category
    builder.addCase(createCategory.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(createCategory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.categories.data = [action.payload, ...state.categories.data];
      state.categories.meta.total = state.categories.meta.total + 1;
    });

    builder.addCase(createCategory.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch category
    builder.addCase(fetchCategories.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchCategories.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.categories = action.payload;
    });

    builder.addCase(fetchCategories.rejected, (state, action) => {
      state.fetchLoading = false;
      // action.payload.enqueueSnackbar(action.payload.error.message, {
      //   variant: "error",
      // });
    });

    // ==========================

    // TODO: fetch single category
    builder.addCase(fetchSingleCategory.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchSingleCategory.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.singleCategory = action.payload;
    });

    builder.addCase(fetchSingleCategory.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: update category
    builder.addCase(updateCategory.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateCategory.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Category is updated successfully");
      // state.categories.data = state.categories.data.map((category) =>
      //   category.slug === action.payload.slug ? action.payload.data : category
      // );
      // console.log(action.payload);
      // action.payload.setOpenEdit(false);
    });

    builder.addCase(updateCategory.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // category → printer assignment
    builder.addCase(fetchCategoryPrinters.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchCategoryPrinters.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.categoryPrinters = action.payload;
    });

    builder.addCase(fetchCategoryPrinters.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    builder.addCase(updateCategoryPrinter.rejected, (state, action) => {
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete category
    builder.addCase(deleteCategory.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteCategory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.categories.data = state.categories.data.filter(
        (category) => category.id !== action.payload.id
      );
      action.payload.enqueueSnackbar("Category is deleted successfully");
    });

    builder.addCase(deleteCategory.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================
  },
});

export default categorySlice.reducer;
